import React, { useEffect, useState } from 'react';

import UsersTable from '../../admin/UsersTable';
import useUsersTableData from '../../admin/useUsersTableData';

import StepShell from './StepShell';

import { useToast } from 'Hooks/useToast';

/**
 * The admin Users table, fed from the step's data endpoint instead of the
 * Blade globals the admin page injects.
 */
function WizardUsersTable({ config }) {
    const { urls, permissions } = config;
    const { roles, departments, costcenters, revDepartments, revCostcenters, venues, organisationUsers, fetchOrganisationUsers, modules } = useUsersTableData({
        rolesUrl: urls.roles,
        placesUrl: urls.places,
        modulesUrl: urls.modules,
        organisationUsersUrl: urls.organisation_users,
    });

    if (roles.length === 0) {
        return <p className="tw-text-sm tw-text-charcoal-50">{$t('Loading…')}</p>;
    }

    return (
        <UsersTable
            embedded
            roles={roles}
            departments={departments}
            costcenters={costcenters}
            revDepartments={revDepartments}
            revCostcenters={revCostcenters}
            venues={venues}
            listUsersUrl={urls.users}
            updateUserUrl={urls.update_user}
            deleteUserUrl={urls.delete_user}
            storeUserUrl={urls.store_user}
            canEdit={permissions.can_edit}
            canCreate={permissions.can_create}
            canEnable={permissions.can_enable}
            canDelete={permissions.can_delete}
            canAssignEventAdmin={permissions.can_assign_event_admin}
            authId={config.auth_id}
            organisationUsers={organisationUsers}
            fetchOrganisationUsers={fetchOrganisationUsers}
            modules={modules}
        />
    );
}

/**
 * Add users. The last required step, so its primary action leads to the
 * finish screen.
 */
export default function UsersStep({ step, urls, deferring, saving, onDefer, onNext }) {
    const [config, setConfig] = useState(null);
    const toast = useToast();

    useEffect(() => {
        axios
            .get(urls.data)
            .then(({ data }) => setConfig(data))
            .catch(() => toast.error($t('Something went wrong. Please try again.')));
    }, [urls.data]);

    const lede = $t('Add the people who will work in this project and assign their roles, departments, venues and modules. More users can be added any time.');

    let content;
    if (!config) {
        content = <p className="tw-text-sm tw-text-charcoal-50">{$t('Loading…')}</p>;
    } else if (!config.permissions.can_list) {
        content = <p className="tw-text-sm tw-text-charcoal-50">{$t('You are not authorised to manage users on this project.')}</p>;
    } else {
        content = (
            // The table is wider than the step panel at most widths.
            <div className="tw-overflow-x-auto">
                <WizardUsersTable config={config} />
            </div>
        );
    }

    return (
        <StepShell step={step} lede={lede} deferring={deferring} saving={saving} onDefer={onDefer} onNext={onNext} nextLabel={$t('Finish required setup')}>
            {content}
        </StepShell>
    );
}
